import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DateTimePicker,
  InputBase,
  Modal,
  Typography,
} from '@ublend-npm/aulaui-core';
import {
  AulaEditor,
  cleanState,
  hasError,
  isPersistable,
  isUpdating,
} from '@ublend-npm/aula-editor';
import { API_URL, FILE_URL } from '@core/constants/endpoints';
import { ManageExtensionStyles } from './ManageExtension.types';

type DateField = 'startDate' | 'endDate' | 'gradesReleasedAt';

type Props = {
  description: string;
  endDate: string;
  gradesReleasedAt: string;
  isHidden: boolean;
  isHandin: boolean;
  isTurnitin: boolean;
  onDescriptionChange: (value: string) => void;
  onEditorStatusChange: (status: {
    isPersistable: boolean;
    isUpdating: boolean;
  }) => void;
  onEndDateChange: (value: string) => void;
  onGradesReleasedAtChange: (value: string) => void;
  onHiddenChange: (value: boolean) => void;
  onRestore: () => void;
  onStartDateChange: (value: string) => void;
  originalDescription: string;
  originalEndDate: string;
  originalGradesReleasedAt: string;
  originalIsHidden: boolean;
  originalStartDate: string;
  spaceId: string;
  startDate: string;
  styles: ManageExtensionStyles;
};

const ExtensionPropertiesStep = ({
  description,
  endDate,
  gradesReleasedAt,
  isHidden,
  isHandin,
  isTurnitin,
  onDescriptionChange,
  onEditorStatusChange,
  onEndDateChange,
  onGradesReleasedAtChange,
  onHiddenChange,
  onRestore,
  onStartDateChange,
  originalDescription,
  originalEndDate,
  originalGradesReleasedAt,
  originalIsHidden,
  originalStartDate,
  spaceId,
  startDate,
  styles,
}: Props) => {
  const [openDateField, setOpenDateField] = useState<DateField>();
  const [pendingDate, setPendingDate] = useState<Date>();
  const editorConfig = useMemo(
    () => ({ apiUrl: API_URL(), filesUrl: FILE_URL(), spaceId }),
    [spaceId]
  );

  const dateValues = { startDate, endDate, gradesReleasedAt };
  const dateChangeHandlers = {
    startDate: onStartDateChange,
    endDate: onEndDateChange,
    gradesReleasedAt: onGradesReleasedAtChange,
  };
  const openDatePicker = (field: DateField) => {
    setPendingDate(
      dateValues[field] ? new Date(dateValues[field]) : new Date()
    );
    setOpenDateField(field);
  };
  const closeDatePicker = () => {
    setOpenDateField(undefined);
    setPendingDate(undefined);
  };
  const formatDate = (value: string) =>
    value ? new Date(value).toLocaleString() : '';
  const getEditorState = () => {
    if (!description) return cleanState;
    try {
      return JSON.parse(description);
    } catch (_error) {
      return cleanState;
    }
  };
  const datesMatch = (current: string, original: string) => {
    if (!current && !original) return true;
    return new Date(current).getTime() === new Date(original).getTime();
  };
  const editedProperties = [
    description !== originalDescription && 'Assignment description',
    !datesMatch(startDate, originalStartDate) && 'Open date',
    !datesMatch(endDate, originalEndDate) && 'Due date',
    !datesMatch(gradesReleasedAt, originalGradesReleasedAt) &&
      'Grades release date',
    isHidden !== originalIsHidden && 'Visibility settings',
  ].filter(Boolean);
  const renderDateField = (field: DateField, label: string) => (
    <Box
      component="label"
      sx={{ ...styles.field, ...styles.dateField }}
      onClick={() => openDatePicker(field)}
    >
      <Typography component="span" sx={styles.fieldLabel}>
        {label}
      </Typography>
      <InputBase
        fullWidth
        readOnly
        value={formatDate(dateValues[field])}
        inputProps={{ 'aria-label': label, readOnly: true }}
      />
    </Box>
  );

  return (
    <Box sx={styles.properties}>
      <Box component="label" sx={styles.field}>
        <Typography component="span" sx={styles.fieldLabel}>
          Assignment Description
        </Typography>
        {isHandin ? (
          <Box sx={styles.descriptionEditor}>
            <AulaEditor
              config={editorConfig}
              contentType="assignmentExtensions"
              editorState={getEditorState()}
              onChange={(value) => {
                onDescriptionChange(JSON.stringify(value));
                onEditorStatusChange({
                  isUpdating: isUpdating(value, editorConfig),
                  isPersistable:
                    isPersistable(value, editorConfig) &&
                    !hasError(value, editorConfig),
                });
              }}
              responsiveFlag
            />
          </Box>
        ) : (
          <InputBase
            multiline
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            fullWidth
          />
        )}
      </Box>
      {renderDateField('startDate', 'Open Date')}
      {renderDateField('endDate', 'Due Date')}
      {isTurnitin && renderDateField('gradesReleasedAt', 'Grades Release Date')}
      <Box sx={styles.switchRow}>
        <Typography>Hide this extension</Typography>
        <Checkbox
          kind="switch"
          checked={isHidden}
          sx={styles.checkboxStyle}
          onChange={() => onHiddenChange(!isHidden)}
          label=""
          aria-label="Hide this extension"
        />
      </Box>
      <Alert severity="info" sx={styles.propertiesInfo}>
        {editedProperties.length ? (
          <Box sx={styles.editedPropertiesAlert}>
            <Typography>
              Properties edited: {editedProperties.join(', ')}
            </Typography>
            <Button kind="secondary" onClick={onRestore}>
              Restore original properties
            </Button>
          </Box>
        ) : (
          <>You haven&apos;t edited any assignment properties yet.</>
        )}
      </Alert>

      <Modal open={Boolean(openDateField)} onClose={closeDatePicker}>
        <Box sx={styles.datePickerContainer}>
          <Typography sx={styles.datePickerTitle}>
            Select date &amp; time
          </Typography>
          <DateTimePicker
            key={openDateField}
            value={pendingDate || new Date()}
            onChange={(date: any) => {
              const value =
                date instanceof Date ? date : date?.toDate?.() ?? null;
              if (value) setPendingDate(value);
            }}
            {...(openDateField === 'startDate' && endDate
              ? { maxDate: new Date(endDate) }
              : {})}
            {...(openDateField === 'endDate' && startDate
              ? { minDate: new Date(startDate) }
              : {})}
            {...(openDateField === 'gradesReleasedAt' && endDate
              ? { minDate: new Date(endDate) }
              : {})}
            sx={styles.dateTimePicker}
          />
          <Box sx={styles.datePickerActions}>
            <Button type="button" kind="secondary" onClick={closeDatePicker}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (openDateField && pendingDate) {
                  dateChangeHandlers[openDateField](
                    new Date(pendingDate).toISOString()
                  );
                }
                closeDatePicker();
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ExtensionPropertiesStep;
